import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Menu, X, BookOpen, Mail, Flame, Tag, Sparkles, Puzzle } from "lucide-react";
import { api, scrollToId } from "@/lib/api";

const SECTION_LINKS = [
  { id: "favoritos", label: "Favoritos", icon: Flame },
  { id: "soluciones", label: "Soluciones", icon: Puzzle },
  { id: "ofertas", label: "Merecen la pena", icon: Tag },
  { id: "novedades", label: "Novedades", icon: Sparkles },
];

export const Header = () => {
  const [categories, setCategories] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const goSection = (id) => {
    setMenuOpen(false);
    if (location.pathname !== "/") navigate(`/#${id}`);
    else scrollToId(id);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (cat) navigate(`/categoria/${cat}`);
    else if (q.trim()) navigate(`/buscar?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      <div className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-4">
          <Link to="/" data-testid="brand-wordmark" className="shrink-0 group flex items-center gap-2.5">
            <img src="/logo.jpg" alt="Logotipo de solsdiarias360" data-testid="brand-logo-img" className="h-11 w-11 rounded-lg bg-white object-cover ring-1 ring-white/20" />
            <span>
              <span className="font-display font-extrabold tracking-tight text-xl sm:text-2xl block leading-none">
                sols<span className="text-amber-400 group-hover:text-amber-300 transition-colors">diarias</span>360
              </span>
              <span className="hidden lg:block text-[10px] uppercase tracking-[0.18em] text-slate-400 font-medium mt-1">
                Soluciones inteligentes para cada día
              </span>
            </span>
          </Link>

          <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-2xl mx-auto" data-testid="search-form">
            <select
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              data-testid="search-category-select"
              className="bg-slate-800 text-slate-200 text-xs rounded-l-lg border border-slate-700 px-2 max-w-[150px] focus:outline-none focus:ring-2 focus:ring-amber-400"
              aria-label="Categoría"
            >
              <option value="">Todas</option>
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="¿Qué solución necesitas hoy?"
              data-testid="search-input"
              className="flex-1 bg-white text-slate-900 text-sm px-4 py-2.5 border-y border-white focus:outline-none focus:ring-2 focus:ring-amber-400 min-w-0"
            />
            <button
              type="submit"
              data-testid="search-submit-button"
              aria-label="Buscar"
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 px-4 rounded-r-lg transition-colors flex items-center"
            >
              <Search size={18} strokeWidth={2.5} />
            </button>
          </form>

          <nav className="hidden md:flex items-center gap-5 text-sm font-medium ml-auto">
            <Link to="/guias" data-testid="nav-guides-link" className="flex items-center gap-1.5 text-slate-300 hover:text-amber-400 transition-colors">
              <BookOpen size={15} /> Guías
            </Link>
            <Link to="/contacto" data-testid="nav-contact-link" className="flex items-center gap-1.5 text-slate-300 hover:text-amber-400 transition-colors">
              <Mail size={15} /> Contacto
            </Link>
          </nav>

          <button
            className="md:hidden ml-auto text-slate-200"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="mobile-menu-button"
            aria-label="Abrir menú"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        <div className="md:hidden px-4 pb-3">
          <form onSubmit={submitSearch} className="flex" data-testid="search-form-mobile">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="¿Qué solución necesitas hoy?"
              data-testid="search-input-mobile"
              className="flex-1 bg-white text-slate-900 text-sm px-4 py-2.5 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-amber-400 min-w-0"
            />
            <button type="submit" data-testid="search-submit-button-mobile" aria-label="Buscar" className="bg-amber-500 text-slate-950 px-4 rounded-r-lg">
              <Search size={18} strokeWidth={2.5} />
            </button>
          </form>
        </div>
      </div>

      <div className="bg-slate-800 text-slate-200 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-1 overflow-x-auto scrollbar-hide md:flex-wrap md:overflow-visible text-xs font-medium">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/categoria/${c.slug}`}
              data-testid={`nav-category-${c.slug}`}
              className="px-3 py-2.5 whitespace-nowrap hover:text-amber-400 hover:bg-slate-700/60 transition-colors rounded"
            >
              {c.name}
            </Link>
          ))}
        </div>
      </div>

      <div className="bg-slate-950 text-slate-200 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-1 overflow-x-auto scrollbar-hide text-xs font-medium">
          {SECTION_LINKS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => goSection(id)}
              data-testid={`nav-section-${id}`}
              className="flex items-center gap-1.5 px-3 py-2.5 whitespace-nowrap hover:text-amber-400 hover:bg-slate-800/80 transition-colors rounded"
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-slate-900 border-t border-slate-800 text-white" data-testid="mobile-menu">
          <div className="px-4 py-3 grid gap-1 text-sm">
            {SECTION_LINKS.map(({ id, label }) => (
              <button key={id} onClick={() => goSection(id)} data-testid={`mobile-section-${id}`} className="text-left px-2 py-2 rounded hover:bg-slate-800 text-slate-200">
                {label}
              </button>
            ))}
            <Link to="/guias" onClick={() => setMenuOpen(false)} data-testid="mobile-guides-link" className="px-2 py-2 rounded hover:bg-slate-800 text-slate-200">Guías y consejos</Link>
            <Link to="/contacto" onClick={() => setMenuOpen(false)} data-testid="mobile-contact-link" className="px-2 py-2 rounded hover:bg-slate-800 text-slate-200">Contacto</Link>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 mt-3 mb-1 px-2">Categorías</p>
            {categories.map((c) => (
              <Link key={c.slug} to={`/categoria/${c.slug}`} onClick={() => setMenuOpen(false)} data-testid={`mobile-category-${c.slug}`} className="px-2 py-2 rounded hover:bg-slate-800 text-slate-300">
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
